import React, { useState, useEffect, useContext } from 'react'
import LinearProgress from '@material-ui/core/LinearProgress'
import { get } from 'lodash'
import { useHistory } from 'react-router-dom'
import { parseEvents } from 'utils/eventUtils'
import {
  ADMTrainingExternalLink,
  GROUP_EVENT_PAGE_LIMIT,
} from 'lib/pga-dot-coach'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/core/styles'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import EventsDisplaySelection from './events-display-selection'
import { RefetchContext } from 'lib/utils/refetchContext'
import DisclaimerWithTwoCta from 'components/disclaimer-with-two-cta'
import { useAuth } from 'lib/auth'
import { useUpgradeFlow } from 'lib/hooks/use-upgrade-flow'
import UpgradeSuccessModal from 'components/upgrade-success-modal'

const useStyles = makeStyles(theme => ({
  tipBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    margin: theme.spacing(2, 3, 0),
    borderRadius: 12,
    backgroundColor: '#E0EAFF',
    border: '1px solid #C5D4FF',
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2, 2, 0),
    },
  },
  tipBannerCopy: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
    flex: '1 1 auto',
  },
  tipBannerIcon: {
    color: '#0053FF',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tipBannerText: {
    fontSize: '0.875rem',
    lineHeight: 1.4,
    color: '#172024',
    margin: 0,
  },
}))

const EventsPageData = ({
  eventsData,
  selectedTab,
  showCanceledEvents,
  offset,
  setOffset,
  showLimitReachedDisclaimer,
  setShowLimitReachedDisclaimer,
  setIsPgaHopeCoach,
}) => {
  const [events, setEvents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [lastPage, setLastPage] = useState(false)
  const history = useHistory()
  const refetch = useContext(RefetchContext)
  const classes = useStyles()
  const { user: coach } = useAuth()
  const { pgaHopeCoach: isPgaHopeCoach } = coach?.coach || {}

  const {
    upgradeLoading: loadingSubscription,
    handleUpgradeClick,
    showSuccessModal,
    handleDismiss,
  } = useUpgradeFlow('group-events')

  useEffect(() => {
    passEventsToState(eventsData, selectedTab)
    const freeTierLimitsReached = get(
      eventsData,
      'currentUser.attributes.freeTierLimitsReached',
      [],
    )
    const isGroupEventsLimitReached = freeTierLimitsReached.includes(
      'GROUP_EVENTS',
    )
    setShowLimitReachedDisclaimer(isGroupEventsLimitReached)
    setIsPgaHopeCoach(isPgaHopeCoach)
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsData, showCanceledEvents, selectedTab])

  const currentPage = offset / GROUP_EVENT_PAGE_LIMIT + 1

  const passEventsToState = (eventsData, selectedTab) => {
    if (eventsData) {
      const inputEvents = get(eventsData, 'currentUser.groupEvents', [])
      const newEvents = parseEvents(inputEvents, selectedTab)
      setEvents(newEvents)
      if (inputEvents.length < GROUP_EVENT_PAGE_LIMIT && offset > 0) {
        setLastPage(true)
      }
    }
  }

  const retreatPagination = async () => {
    const newOffset =
      offset - GROUP_EVENT_PAGE_LIMIT < 0 ? 0 : offset - GROUP_EVENT_PAGE_LIMIT
    setOffset(newOffset)
    await refetch({ offset: newOffset })
    setLastPage(false)
  }
  const advancePagination = async () => {
    const newOffset = offset + GROUP_EVENT_PAGE_LIMIT
    setOffset(newOffset)
    await refetch({ offset: newOffset })
  }

  const { canPromoteContent } = eventsData.currentUser.attributes
  const totalEvents = get(eventsData, 'currentUser.groupEvents', []).length
  const showEmptyState = events.length === 0

  if (isLoading) return <LinearProgress color="primary" />
  return (
    <>
      {!canPromoteContent && !showEmptyState && (
        <Box className={classes.tipBanner}>
          <Box className={classes.tipBannerCopy}>
            <InfoOutlinedIcon
              className={classes.tipBannerIcon}
              fontSize="small"
              opticalSize={20}
            />
            <Typography component="span" className={classes.tipBannerText}>
              You are currently ineligible to promote your events on PGA.com.
              Please complete <ADMTrainingExternalLink icon={false} /> to become
              eligible.
            </Typography>
          </Box>
        </Box>
      )}
      <Box mx={[2, 2, 6]} mt={[2, 4]} mb={[3, 3, 0]}>
        {loadingSubscription && <LinearProgress color="primary" />}
        <DisclaimerWithTwoCta
          description="Enjoy the benefits of your free account with up to two active group events. Want to host more? Upgrade now to unlock unlimited group offerings."
          leftCta={
            <Button variant="text" onClick={handleUpgradeClick}>
              Upgrade Now
            </Button>
          }
          rightCta={
            <Button
              variant="text"
              onClick={() => {
                setShowLimitReachedDisclaimer(false)
              }}
            >
              Got It
            </Button>
          }
          show={showLimitReachedDisclaimer}
        />
      </Box>
      <EventsDisplaySelection
        events={events}
        totalEvents={totalEvents}
        history={history}
        selectedTab={selectedTab}
        advancePagination={advancePagination}
        retreatPagination={retreatPagination}
        currentPage={currentPage}
        lastPage={lastPage}
      />

      <UpgradeSuccessModal isOpen={showSuccessModal} onClose={handleDismiss} />
    </>
  )
}
export default EventsPageData
